import request from '@/utils/http';
import { prefix } from '@/utils/prefix';
import { unwrapResponse } from '@/utils/http/types';
/**
 * 获取流程定义列表
 */
export async function getFlowDefinitions() {
  return unwrapResponse(request.get(`${prefix.flow}/flow/definitions`));
}

/**
 * 分页查询流程定义（POST + body，因后端 controller 用 @Body() 接收）
 */
export async function getFlowDefinitionPage(query) {
  return unwrapResponse(request.post(`${prefix.flow}/flow/definition/page`, query));
}

/**
 * 查询单个流程定义（含 xml）
 */
export async function getFlowDefinition(id) {
  return unwrapResponse(request.get(`${prefix.flow}/flow/definition/${id}`));
}

/**
 * 创建流程定义（前端设计器保存）
 */
export async function createFlowDefinition(dto) {
  return unwrapResponse(request.post(`${prefix.flow}/flow/definition`, dto));
}

/**
 * 更新流程定义
 */
export async function updateFlowDefinition(id, dto) {
  return unwrapResponse(request.put(`${prefix.flow}/flow/definition/${id}`, dto));
}

/**
 * 删除流程定义（有运行中实例时会被后端拒绝）
 */
export async function deleteFlowDefinition(id) {
  return unwrapResponse(request.delete(`${prefix.flow}/flow/definition/${id}`));
}

/**
 * 通过定义 ID 获取 raw BPMN xml（后端 @Header text/xml 返回纯字符串）。
 * 注意：这个接口走 @Res() 返回，不是 ApiResponse 包装格式，
 *       不能走 unwrapResponse，直接拿 response.data 就是 xml 字符串。
 */
export async function getFlowDefinitionXml(id, version) {
  const url = `${prefix.flow}/flow/definition/${id}/xml${version !== undefined ? `?version=${version}` : ''}`;
  const res = await request.get(url);
  return res ?? '';
}

/**
 * 部署/发布当前草稿：产生新版本，写历史。
 * 后端 @Post('definition/:id/deploy') @Body() body: { remark?: string }
 */
export async function deployFlowDefinition(id, remark) {
  return unwrapResponse(request.post(`${prefix.flow}/flow/definition/${id}/deploy`, {
    remark: remark ?? null
  }));
}

/**
 * 启动流程
 */
export async function startFlow(data) {
  return unwrapResponse(request.post(`${prefix.flow}/flow/start`, data));
}

/**
 * 审批（通过/驳回/退回）
 */
export async function approveFlow(data) {
  return unwrapResponse(request.post(`${prefix.flow}/flow/approve`, data));
}

/**
 * 我的待办
 */
export async function getMyTodo(userId, page = 1, pageSize = 10, searchParams) {
  return unwrapResponse(request.get(`${prefix.flow}/flow/todo/${userId}`, {
    params: {
      page,
      pageSize,
      ...searchParams
    }
  }));
}

/**
 * 我的已办
 */
export async function getMyDone(userId, page = 1, pageSize = 10, searchParams) {
  return unwrapResponse(request.get(`${prefix.flow}/flow/done/${userId}`, {
    params: {
      page,
      pageSize,
      ...searchParams
    }
  }));
}

/**
 * 我发起的流程
 */
export async function getMyInitiated(userId, page = 1, pageSize = 10, searchParams) {
  return unwrapResponse(request.get(`${prefix.flow}/flow/initiated/${userId}`, {
    params: {
      page,
      pageSize,
      ...searchParams
    }
  }));
}

/**
 * 流程详情
 */
export async function getFlowDetail(instanceId) {
  return unwrapResponse(request.get(`${prefix.flow}/flow/detail/${instanceId}`));
}

/**
 * 流程进度（已走过/当前/未到节点）
 */
export async function getFlowProgress(bizId) {
  return unwrapResponse(request.get(`${prefix.flow}/flow/progress/biz/${bizId}`));
}

/**
 * 获取可退回的节点列表（当前节点之前的 userTask 节点）
 */
export async function getReturnableNodes(instanceId) {
  return unwrapResponse(request.get(`${prefix.flow}/flow/returnable-nodes/${instanceId}`));
}

/**
 * 流程审批记录（历史轨迹）
 */
export async function getTaskHistory(bizId) {
  return unwrapResponse(request.get(`${prefix.flow}/flow/history/biz/${bizId}`));
}

/**
 * 分页查询业务数据
 */
export async function getBizDataPage(data) {
  return unwrapResponse(request.post(`${prefix.flow}/flow/biz/page`, data));
}

/**
 * 业务数据详情
 */
export async function getBizDataDetail(id) {
  return unwrapResponse(request.get(`${prefix.flow}/flow/biz/${id}`));
}

/**
 * 废弃业务数据
 */
export async function discardBizData(id) {
  return unwrapResponse(request.post(`${prefix.flow}/flow/biz/discard/${id}`));
}

/**
 * 获取审批人信息
 * @param bizId
 * @returns
 */
export async function getAssignees(bizId) {
  return unwrapResponse(request.get(`${prefix.flow}/flow/assignees/biz/${bizId}`));
}